import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { ChevronDown, ChevronUp, Search, Trash2 } from 'lucide-react';
import exercisesData from '../data/exercises.json';
import { deleteWorkout, deleteExerciseFromWorkout } from '../services/logService';
import toast from 'react-hot-toast';

function WorkoutTimeline() {
  const { user } = useAuth();
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    const loadWorkouts = async () => {
      if (!user) return;
      try {
        const workoutsRef = collection(db, 'users', user.uid, 'workouts');
        const q = query(workoutsRef, orderBy('completedAt', 'desc'), limit(30));
        const snapshot = await getDocs(q);
        const workoutData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setWorkouts(workoutData);
      } catch (error) {
        console.error('Error loading workouts:', error);
      } finally {
        setLoading(false);
      }
    };
    loadWorkouts();
  }, [user]);

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const handleDeleteWorkout = async (e, workoutId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this workout? This cannot be undone.')) return;

    try {
      await deleteWorkout(user.uid, workoutId);
      setWorkouts(workouts.filter(w => w.id !== workoutId));
      toast.success('Workout deleted');
    } catch (error) {
      console.error('Error deleting workout:', error);
      toast.error('Failed to delete workout');
    }
  };

  const handleDeleteExercise = async (e, workoutId, exerciseIndex) => {
    e.stopPropagation();
    if (!window.confirm('Delete this exercise from the workout?')) return;

    try {
      const result = await deleteExerciseFromWorkout(user.uid, workoutId, exerciseIndex);

      if (result === null) {
        // No exercises left, ask if they want to delete the whole workout
        if (window.confirm('This was the last exercise. Delete the entire workout?')) {
          await deleteWorkout(user.uid, workoutId);
          setWorkouts(workouts.filter(w => w.id !== workoutId));
          toast.success('Workout deleted');
        }
      } else {
        // Update the local state with the modified exercises array
        setWorkouts(workouts.map(w =>
          w.id === workoutId ? { ...w, exercises: result } : w
        ));
        toast.success('Exercise deleted');
      }
    } catch (error) {
      console.error('Error deleting exercise:', error);
      toast.error('Failed to delete exercise');
    }
  };

  if (loading) {
    return <div className="text-center py-8 font-plex text-light-ink dark:text-dark-ink">Loading...</div>;
  }

  if (workouts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-light-muted dark:text-dark-muted mb-4 font-plex text-[15px]">No workouts yet</p>
        <Link to="/" className="text-light-accent dark:text-dark-accent hover:underline font-plex text-[15px]">
          Start with an Adhoc Session
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {workouts.map(workout => {
        const isExpanded = expandedIds.has(workout.id);
        const date = workout.completedAt?.toDate?.() || new Date();
        const completedSets = workout.exercises?.reduce((sum, ex) => {
          return sum + (ex.sets?.filter(s => s.done).length || 0);
        }, 0) || 0;
        const totalSets = workout.exercises?.reduce((sum, ex) => sum + (ex.sets?.length || 0), 0) || 0;

        return (
          <div key={workout.id} className="border border-light-border dark:border-dark-border rounded-[14px] overflow-hidden">
            <div className="flex items-center bg-light-surface dark:bg-dark-surface">
              <button
                onClick={() => toggleExpand(workout.id)}
                className="flex-1 px-4 py-3 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-plex font-semibold text-[15px] text-light-ink dark:text-dark-ink capitalize">
                        {workout.focus}
                      </span>
                      <span className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                        {date.toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted mt-1">
                      Tier {workout.tier} · {workout.minutes} min · {completedSets} of {totalSets} sets
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp size={20} className="text-light-muted dark:text-dark-muted" />
                  ) : (
                    <ChevronDown size={20} className="text-light-muted dark:text-dark-muted" />
                  )}
                </div>
              </button>
              <button
                onClick={(e) => handleDeleteWorkout(e, workout.id)}
                className="px-3 py-3 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                title="Delete workout"
              >
                <Trash2 size={18} className="text-light-muted dark:text-dark-muted hover:text-red-600 dark:hover:text-red-400" />
              </button>
            </div>

            {isExpanded && workout.exercises && (
              <div className="px-4 py-3 bg-light-bg dark:bg-dark-bg border-t border-light-border dark:border-dark-border">
                <div className="space-y-3">
                  {workout.exercises.map((ex, idx) => {
                    const exerciseDetails = exercisesData.find(e => e.id === ex.exerciseId);
                    return (
                      <div key={idx} className="text-[13px] font-plex">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="font-semibold text-light-ink dark:text-dark-ink flex-1">
                            {ex.name || exerciseDetails?.name}
                          </div>
                          <button
                            onClick={(e) => handleDeleteExercise(e, workout.id, idx)}
                            className="p-1 hover:bg-red-50 dark:hover:bg-red-950 rounded transition-colors"
                            title="Delete exercise"
                          >
                            <Trash2 size={14} className="text-light-muted dark:text-dark-muted hover:text-red-600 dark:hover:text-red-400" />
                          </button>
                        </div>
                        <div className="space-y-1">
                          {ex.sets?.map((set, setIdx) => {
                            if (!set.done) return null;
                            const parts = [];
                            if (set.weight != null) parts.push(`${set.weight} ${set.unit || 'lb'}`);
                            if (set.reps != null) parts.push(`${set.reps} reps`);
                            if (set.durationSeconds != null) parts.push(`${set.durationSeconds}s`);
                            return (
                              <div key={setIdx} className="text-light-muted dark:text-dark-muted">
                                Set {setIdx + 1}: {parts.join(' × ') || 'Completed'}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ExerciseProgress() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [exerciseLogs, setExerciseLogs] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadExerciseLogs = async () => {
      if (!user) return;
      try {
        const logRef = collection(db, 'users', user.uid, 'exerciseLog');
        const snapshot = await getDocs(logRef);
        const logs = snapshot.docs.map(doc => ({
          exerciseId: doc.id,
          ...doc.data(),
        }));
        setExerciseLogs(logs);
      } catch (error) {
        console.error('Error loading exercise logs:', error);
      } finally {
        setLoading(false);
      }
    };
    loadExerciseLogs();
  }, [user]);

  const filteredLogs = exerciseLogs.filter(log => {
    const exercise = exercisesData.find(e => e.id === log.exerciseId);
    return exercise?.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return <div className="text-center py-8 font-plex text-light-ink dark:text-dark-ink">Loading...</div>;
  }

  if (exerciseLogs.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-light-muted dark:text-dark-muted font-plex text-[15px]">No exercise history yet</p>
      </div>
    );
  }

  if (selectedExercise) {
    const exercise = exercisesData.find(e => e.id === selectedExercise.exerciseId);
    const entries = selectedExercise.entries || [];

    return (
      <div className="space-y-6">
        <button
          onClick={() => setSelectedExercise(null)}
          className="text-light-accent dark:text-dark-accent hover:underline text-[13px] font-plex"
        >
          ← Back to exercises
        </button>

        <div>
          <h2 className="text-[20px] font-grotesk font-bold text-light-ink dark:text-dark-ink">{exercise?.name}</h2>
          <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted mt-1">
            {selectedExercise.totalSessions} session{selectedExercise.totalSessions !== 1 ? 's' : ''}
          </div>
        </div>

        {entries.length < 2 ? (
          <div className="bg-light-bg dark:bg-dark-bg rounded-[14px] p-6 text-center">
            <div className="text-[24px] font-grotesk font-bold tabular-nums text-light-ink dark:text-dark-ink">
              {entries[0]?.topWeight ? `${entries[0].topWeight} ${entries[0].unit}` : 'No data'}
            </div>
            {entries[0]?.topReps && (
              <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted mt-1">{entries[0].topReps} reps</div>
            )}
          </div>
        ) : (
          <div className="bg-light-bg dark:bg-dark-bg rounded-[14px] p-6">
            <ProgressChart entries={entries} />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-[14px] p-4">
            <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted mb-1">Best Set</div>
            <div className="text-[17px] font-grotesk font-semibold tabular-nums text-light-ink dark:text-dark-ink">
              {selectedExercise.bestWeight} {selectedExercise.bestWeightUnit}
            </div>
          </div>
          <div className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-[14px] p-4">
            <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted mb-1">Current Weight</div>
            <div className="text-[17px] font-grotesk font-semibold tabular-nums text-light-ink dark:text-dark-ink">
              {entries[entries.length - 1]?.topWeight || '—'} {entries[entries.length - 1]?.unit || ''}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-light-muted dark:text-dark-muted" size={20} />
        <input
          type="text"
          placeholder="Search exercises..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-light-border dark:border-dark-border rounded-[14px] bg-light-surface dark:bg-dark-surface text-light-ink dark:text-dark-ink font-plex text-[15px] placeholder:text-light-muted dark:placeholder:text-dark-muted"
        />
      </div>

      <div className="space-y-2">
        {filteredLogs.map(log => {
          const exercise = exercisesData.find(e => e.id === log.exerciseId);
          const latestEntry = log.entries?.[log.entries.length - 1];
          return (
            <button
              key={log.exerciseId}
              onClick={() => setSelectedExercise(log)}
              className="w-full text-left px-4 py-3 bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-[14px] hover:border-light-ink dark:hover:border-dark-accent transition-colors"
            >
              <div className="font-plex font-semibold text-[15px] text-light-ink dark:text-dark-ink">{exercise?.name}</div>
              <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted mt-1">
                {log.totalSessions} session{log.totalSessions !== 1 ? 's' : ''} · Last: {latestEntry?.topWeight} {latestEntry?.unit}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ProgressChart({ entries }) {
  const weights = entries.map(e => e.topWeight || 0);
  const maxWeight = Math.max(...weights);
  const minWeight = Math.min(...weights);
  const range = maxWeight - minWeight || 1;

  const width = 400;
  const height = 200;
  const padding = 40;

  const points = entries.map((entry, i) => {
    const x = padding + (i / (entries.length - 1)) * (width - 2 * padding);
    const y = height - padding - ((entry.topWeight - minWeight) / range) * (height - 2 * padding);
    return { x, y, entry };
  });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
      <polyline
        points={points.map(p => `${p.x},${p.y}`).join(' ')}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="text-light-accent dark:text-dark-accent"
      />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill="currentColor" className="text-light-accent dark:text-dark-accent" />
      ))}
      <text x="10" y="30" fontSize="12" fill="currentColor" className="text-light-muted dark:text-dark-muted font-grotesk tabular-nums">
        {maxWeight.toFixed(1)}
      </text>
      <text x="10" y={height - 20} fontSize="12" fill="currentColor" className="text-light-muted dark:text-dark-muted font-grotesk tabular-nums">
        {minWeight.toFixed(1)}
      </text>
    </svg>
  );
}

function History() {
  const [activeTab, setActiveTab] = useState('timeline');

  return (
    <div className="min-h-screen-safe bg-light-bg dark:bg-dark-bg px-4 py-6 pb-24">
      <div className="bg-light-surface dark:bg-dark-surface rounded-[18px]">
        <div className="border-b border-light-border dark:border-dark-border">
          <div className="flex">
            <button
              onClick={() => setActiveTab('timeline')}
              className={`flex-1 py-4 text-center font-plex font-semibold text-[15px] transition-colors ${
                activeTab === 'timeline'
                  ? 'text-light-ink dark:text-dark-ink border-b-2 border-light-accent dark:border-dark-accent'
                  : 'text-light-muted dark:text-dark-muted hover:text-light-ink dark:hover:text-dark-ink'
              }`}
            >
              Workout Timeline
            </button>
            <button
              onClick={() => setActiveTab('progress')}
              className={`flex-1 py-4 text-center font-plex font-semibold text-[15px] transition-colors ${
                activeTab === 'progress'
                  ? 'text-light-ink dark:text-dark-ink border-b-2 border-light-accent dark:border-dark-accent'
                  : 'text-light-muted dark:text-dark-muted hover:text-light-ink dark:hover:text-dark-ink'
              }`}
            >
              Exercise Progress
            </button>
          </div>
        </div>

        <div className="p-4">
          {activeTab === 'timeline' ? <WorkoutTimeline /> : <ExerciseProgress />}
        </div>
      </div>
    </div>
  );
}

export default History;
